/**
 * Fine Square – Marmovella: Italian mineral stone finish.
 * Content transcribed from the official Vertex shade card, finish sheets and
 * application procedure (Sep 2026). Swatch tiles are cropped from the shade card.
 */

const M = '/images/marmovella/';

export type MarmovellaShade = { name: string; image: string; note?: string };
export type MarmovellaApplyPhoto = { image: string; caption: string };
export type MarmovellaFamily = {
  id: 'classico' | 'granito' | 'lusso';
  number: number;
  name: string;
  tagline: string;
  description: string;
  bestFor: string[];
  suitability: string;
  lookAndFeel: { title: string; text: string }[];
  whyChoose: string[];
  apply: MarmovellaApplyPhoto[];
  shades: MarmovellaShade[];
};

export const MARMOVELLA: MarmovellaFamily[] = [
  {
    id: 'classico',
    number: 1,
    name: 'Marmovella Classico',
    tagline: 'Natural coloured-mineral finish',
    description:
      'Classico brings the timeless beauty of natural mineral walls into your spaces. Its subtle texture and soft stone character create an elegant, everyday finish that feels warm, calming and refined. The balanced visual richness of Classico shades makes them easy to like, easy to live with.',
    bestFor: ['Living rooms', 'Bedrooms', 'Corridors', 'Staircases', 'Balcony walls', 'Entrance areas', 'Feature walls', 'Selected exterior walls'],
    suitability: 'Suitable for interior & exterior',
    lookAndFeel: [
      { title: 'Natural', text: 'Inspired by natural stone.' },
      { title: 'Subtle', text: 'Soft texture, calm appearance.' },
      { title: 'Refined', text: 'Elegant finish for everyday spaces.' },
      { title: 'Comfortable', text: 'An inviting look you will enjoy every day.' },
    ],
    whyChoose: ['Natural stone feel for timeless beauty', 'Versatile shades that suit any style', 'Elegant daily-use finish for all spaces', 'Ideal for homes and large projects', 'Easy to coordinate with furniture and decor'],
    apply: [
      { image: `${M}classico-living.jpg`, caption: 'Warm and welcoming living spaces' },
      { image: `${M}classico-staircase.jpg`, caption: 'Corridors and staircases with character' },
      { image: `${M}classico-exterior.jpg`, caption: 'Exterior walls with natural elegance' },
    ],
    shades: [
      { name: 'Nero Granito', image: `${M}classico-nero-granito.jpg` },
      { name: 'Perla Bianca', image: `${M}classico-perla-bianca.jpg` },
      { name: 'Sabbia Beige', image: `${M}classico-sabbia-beige.jpg` },
      { name: 'Crema Pesca', image: `${M}classico-crema-pesca.jpg` },
      { name: 'Argento Grey', image: `${M}classico-argento-grey.jpg` },
      { name: 'Rosso Terra', image: `${M}classico-rosso-terra.jpg` },
    ],
  },
  {
    id: 'granito',
    number: 2,
    name: 'Marmovella Granito',
    tagline: 'Rich multi-granule stone texture',
    description:
      'Granito delivers a bold, dynamic and natural stone look with larger, visible mineral grains. It creates deeper texture, richer movement and a more pronounced decorative impact compared to Classico — perfect for statement spaces that demand presence.',
    bestFor: ['Exterior elevations', 'Entrance walls', 'Staircase cores', 'Boundary walls', 'Columns', 'Lobby walls', 'Feature walls', 'Selected interior accent walls'],
    suitability: 'Best for interior & exterior — especially strong for exterior statement walls',
    lookAndFeel: [
      { title: 'Bold', text: 'Large mineral grains create strong visual impact.' },
      { title: 'Rich', text: 'Deep texture with natural colour movement.' },
      { title: 'Textured', text: 'A granular surface that feels naturally stone-like.' },
      { title: 'Stone-like', text: 'An authentic mineral impression for lasting beauty.' },
    ],
    whyChoose: ['Stronger visual depth', 'Ideal for grander spaces', 'Hides minor surface unevenness', 'Durable decorative impact', 'Premium stone impression for lasting elegance'],
    apply: [
      { image: `${M}granito-elevation.jpg`, caption: 'Exterior elevations with bold character' },
      { image: `${M}granito-lobby.jpg`, caption: 'Lobby and entry walls with rich texture' },
      { image: `${M}granito-staircase.jpg`, caption: 'Staircase cores and commercial corridors' },
    ],
    shades: [
      { name: 'Tuscan Gold', image: `${M}granito-tuscan-gold.jpg` },
      { name: 'Oro Miele', image: `${M}granito-oro-miele.jpg` },
      { name: 'Burgundy Stone', image: `${M}granito-burgundy-stone.jpg` },
      { name: 'Pistachio Verde', image: `${M}granito-pistachio-verde.jpg` },
      { name: 'Bosco Verde', image: `${M}granito-bosco-verde.jpg` },
      { name: 'Cioccolato', image: `${M}granito-cioccolato.jpg` },
    ],
  },
  {
    id: 'lusso',
    number: 3,
    name: 'Marmovella Lusso',
    tagline: 'Luxury sparkle mineral finish',
    description:
      'Marmovella Lusso is a premium decorative finish with radiant mineral sparkle effects. It delivers a more glamorous, light-reflective look that brings depth and elegance to your walls, making every space feel special and luxurious. Available in silver sparkle and golden sparkle finishes.',
    bestFor: ['TV back walls', 'Dining feature walls', 'Lounge walls', 'Reception areas', 'Hotel-style bedrooms', 'Premium niches and panels', 'Pooja or statement corners', 'Sheltered accent walls'],
    suitability: 'Recommended mainly for premium interior spaces and protected accent areas',
    lookAndFeel: [
      { title: 'Sparkling', text: 'Mineral sparkle that adds a touch of glamour.' },
      { title: 'Luxurious', text: 'Elevates interiors with a rich, high-end feel.' },
      { title: 'Luminous', text: 'Reflects light beautifully for a radiant look.' },
      { title: 'Decorative', text: 'Ideal for statement walls and special spaces.' },
    ],
    whyChoose: ['A bold, elegant statement look', 'Sparkle that catches the light', 'Adds richness and sophistication', 'A unique decorative effect', 'Perfect for highlights and focal walls'],
    apply: [
      { image: `${M}lusso-living.jpg`, caption: 'Luxury living-room accent wall' },
      { image: `${M}lusso-reception.jpg`, caption: 'Reception or console backdrop' },
      { image: `${M}lusso-bedroom.jpg`, caption: 'Premium bedroom or dining feature wall' },
    ],
    shades: [
      { name: 'Blu Profondo', image: `${M}lusso-blu-profondo.jpg`, note: 'Silver sparkle' },
      { name: 'Cielo Azzurro', image: `${M}lusso-cielo-azzurro.jpg`, note: 'Silver sparkle' },
      { name: 'Rosa Passione', image: `${M}lusso-rosa-passione.jpg`, note: 'Golden sparkle' },
      { name: 'Verde Acquamarina', image: `${M}lusso-verde-acquamarina.jpg`, note: 'Golden sparkle' },
    ],
  },
];

export const MARMOVELLA_HIGHLIGHTS = ['Natural stone inspired', 'Durable and long lasting', 'Water repellent', 'For interiors and exteriors', 'Low-maintenance beauty'];

export const MARMOVELLA_STEPS = [
  { title: 'Surface preparation', text: 'The surface should be sound, clean, dry and free from loose material.' },
  { title: 'Putty / repair where required', text: 'Fill cracks and level imperfections.' },
  { title: 'Primer', text: 'Apply one coat of a suitable Vertex primer or sealer.' },
  { title: 'Base coat', text: 'Apply the matching Marmovella base-coat shade.' },
  { title: 'Mixing', text: 'Mix the Marmovella material thoroughly before use.' },
  { title: 'Application', text: 'Apply with a steel trowel or spatula. Classico gives a subtle stone finish, Granito a bolder grainy finish and Lusso a sparkle effect.' },
  { title: 'Drying', text: 'Allow proper drying between coats.' },
  { title: 'Protective top coat', text: 'Apply a clear protective coat wherever recommended. It enhances durability and ease of maintenance.' },
];

export const MARMOVELLA_TOOLS = ['Steel trowel', 'Spatula', 'Mixing paddle', 'Roller or brush for primer and base coat', 'Masking tape', 'Clean bucket'];
export const MARMOVELLA_SUBSTRATES = ['Interior plaster walls', 'Exterior plaster walls', 'Prepared putty walls', 'Concrete surfaces', 'Architectural feature panels'];
export const MARMOVELLA_TIPS = [
  { title: 'Make a sample panel first', text: 'Always test on a small area to confirm the look and texture.' },
  { title: 'Maintain shade consistency', text: 'Ensure uniform mixing and application for a consistent shade.' },
  { title: 'Use the same batch on one area', text: 'Use the same batch on one continuous area wherever possible.' },
  { title: 'Protect from dust and water while curing', text: 'Avoid dust, moisture and rain until the finish is fully cured.' },
];
export const MARMOVELLA_COVERAGE = [
  'Actual coverage depends on texture, surface condition and technique.',
  'Different Marmovella finish families create different visual depth and texture character.',
  'Choose the finish according to the desired look, substrate and application style.',
  'The final look depends on lighting and applicator technique.',
];

export const MARMOVELLA_WARRANTY = {
  years: 11,
  note: 'Approved system required for warranty: primer, base coat, Marmovella and protective coat.',
};
